import { PUBLIC_INPUTS, PUBLIC_SCHEMAS, PUBLIC_TOOLS, validatePublic } from "../../../../lib/copy/src/agent-contracts.mjs";
import { AGENT_FILL_EVENT, readAgentSelection, type AgentSelection } from "./agent-prefill";
const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;
interface ModelContext { registerTool(tool:unknown,options?:{signal:AbortSignal}):void; unregisterTool?(name:string):void }
export function modelContext():ModelContext|undefined {
  return [(document as Document & {modelContext?:ModelContext}).modelContext,
    (navigator as Navigator & {modelContext?:ModelContext}).modelContext]
    .find(candidate=>typeof candidate?.registerTool==="function");
}
const registrations=new WeakMap<ModelContext,Set<string>>();
export function registerBrowserTools(carId?:number) {
  const context=modelContext();
  if(!context)return ()=>{};
  const controller=new AbortController();
  const owned:string[]=[];
  const existing=registrations.get(context) ?? new Set<string>();registrations.set(context,existing);
  const read=async(path:string,output:string)=>{
    const response=await fetch(`${API_BASE}${path}`,{credentials:"omit",headers:{accept:"application/json"},signal:AbortSignal.any([controller.signal,AbortSignal.timeout(8000)])});
    const body=await response.json();
    if(!response.ok)return body;
    const error=validatePublic(PUBLIC_SCHEMAS[output],body,"response");
    if(error)throw new Error("Public reader returned incomplete evidence; retry later.");
    return body;
  };
  const add=(name:string,inputSchema:any,description:string,execute:(args:any)=>unknown)=>{
    if(existing.has(name))return;
    try {
      context.registerTool({name,description,inputSchema,
        annotations:{readOnlyHint:name!=="fill_booking_form",destructiveHint:false,idempotentHint:true,openWorldHint:false},
        ...(name==="search_cars"?{outputSchema:PUBLIC_SCHEMAS.Search}:name==="get_quote"?{outputSchema:PUBLIC_SCHEMAS.Quote}:{}),
        execute:async(args:unknown)=>{
        if(controller.signal.aborted)throw new Error("Page navigation cancelled this tool.");
        const problem=validatePublic(inputSchema,args);
        if(problem)throw new Error(problem);
        return execute(args);
      }},{signal:controller.signal});
      existing.add(name);owned.push(name);
    } catch { /* An experimental API may reject registration; normal UI is unaffected. */ }
  };
  if(carId==null) {
    add("search_cars",PUBLIC_INPUTS.search_cars,PUBLIC_TOOLS.find(tool=>tool.name==="search_cars")!.description,
      args=>read(`/cars/search?${new URLSearchParams({...args,representation:"agent"})}`,"Search"));
  } else {
    const {car_id,...quoteProps}=PUBLIC_INPUTS.get_quote.properties;
    add("get_quote",{...PUBLIC_INPUTS.get_quote,properties:quoteProps,required:["date_from","date_to"]},
      "Anonymous estimate for this car only. Integer hours for chauffeured/event; no reservation or payment action.",
      args=>read(`/bookings/quote?${new URLSearchParams({...args,car_id:String(carId),representation:"agent"})}`,"Quote"));
    const {id,...fillProps}=PUBLIC_INPUTS.get_car_details.properties;
    add("fill_booking_form",{type:"object",properties:fillProps,required:["start","end","hire"],additionalProperties:false},
      "Fill dates, hire type and optional hours for this car; scroll the visible widget. Never submit, authenticate, book, pay or message.",
      args=>{
        const selection=readAgentSelection(new URLSearchParams(args));
        if(!selection.start || !selection.end || !selection.hire)throw new Error("Send valid start, end and hire.");
        return new Promise((resolve,reject)=>{
          const timer=setTimeout(()=>reject(new Error("The visible booking widget is not ready; try again after it loads.")),600);
          const acknowledge=(accepted:boolean)=>{
            clearTimeout(timer);
            if(!accepted){reject(new Error("This car does not support the requested hire type or duration."));return;}
            requestAnimationFrame(()=>requestAnimationFrame(()=>{
              if(controller.signal.aborted){reject(new Error("Navigation cancelled this fill."));return;}
              const widgets=[...document.querySelectorAll<HTMLElement>("[data-agent-booking-widget]")];
              const visible=widgets.find(widget=>widget.getBoundingClientRect().height>0);
              visible?.scrollIntoView({block:"center",behavior:"smooth"});
              resolve({filled:true,selection,submitted:false,message:"Review current pricing/availability yourself before signing in and checking out."});
            }));
          };
          window.dispatchEvent(new CustomEvent(AGENT_FILL_EVENT,{detail:{carId,selection:selection as AgentSelection,acknowledge}}));
        });
      });
  }
  return ()=>{
    controller.abort();
    for(const name of owned){try{context.unregisterTool?.(name);}catch{}existing.delete(name);}
  };
}
